import type { BodyRegion, ImageAsset, ImageRole, Point, Side } from './types'

export const APP_VERSION = '0.1.0'

export function uid(prefix = 'id'): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}_${crypto.randomUUID()}`
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export function formatDate(value: string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!value) return 'Data sconosciuta'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Data sconosciuta'
  return new Intl.DateTimeFormat('it-IT', options ?? { day: '2-digit', month: 'short', year: 'numeric' }).format(date)
}

export function formatDateTime(value: string | null | undefined): string {
  return formatDate(value, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function bodyRegionLabel(region: BodyRegion): string {
  const labels: Record<BodyRegion, string> = {
    testa: 'Testa / viso', collo: 'Collo', torace: 'Torace', schiena: 'Schiena', addome: 'Addome',
    braccio: 'Braccio', avambraccio: 'Avambraccio', mano: 'Mano', coscia: 'Coscia', gamba: 'Gamba',
    piede: 'Piede', 'non-riconosciuta': 'Non riconosciuta',
  }
  return labels[region]
}

export function sideLabel(side: Side): string {
  return ({ sinistro: 'Sinistro', destro: 'Destro', centrale: 'Centro', 'non-specificato': 'Non specificato' })[side]
}

export function positionLabel(region: BodyRegion, side: Side): string {
  return `${bodyRegionLabel(region)} · ${sideLabel(side)}`
}

export function roleLabel(role: ImageRole): string {
  return role === 'detail' ? 'Foto del dettaglio' : 'Foto informativa della posizione'
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y)
}

export function fileExtension(mime: string): string {
  return mime.split('/')[1]?.split(';')[0] || 'bin'
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Impossibile leggere il file'))
    reader.readAsDataURL(blob)
  })
}

export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl)
  return response.blob()
}

export async function sha256(blob: Blob): Promise<string> {
  const bytes = await blob.arrayBuffer()
  const hash = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(hash)).map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function imageDimensions(blob: Blob): Promise<{ width: number | null; height: number | null }> {
  if (typeof Image === 'undefined' || typeof URL === 'undefined') return Promise.resolve({ width: null, height: null })
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: image.naturalWidth || null, height: image.naturalHeight || null })
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      resolve({ width: null, height: null })
    }
    image.src = url
  })
}

export function imageUrl(image: Pick<ImageAsset, 'blob'> | null | undefined): string | null {
  if (!image) return null
  return URL.createObjectURL(image.blob)
}

export function revokeImageUrl(url: string | null): void {
  if (url) URL.revokeObjectURL(url)
}
